// src/pages/AISentryV2.jsx - AI-Enhanced Network Topology
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_KEY = process.env.REACT_APP_API_KEY || '';
const apiKeyHeader = API_KEY ? { 'X-API-Key': API_KEY } : {};

const AISentryV2 = () => {
  const [edgeNodes, setEdgeNodes] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [viewMode, setViewMode] = useState('live');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [stats, setStats] = useState({ online: 0, offline: 0, devices: 0, alerts: 0 });
  const svgRef = useRef(null);

  // Mock AI Actions
  const [aiActions] = useState([
    {
      id: 1,
      timestamp: new Date(Date.now() - 2 * 60000),
      type: 'ISOLATION',
      icon: '🛡️',
      device: '192.168.5.53',
      deviceType: 'PLC',
      reason: 'Unauthorized Modbus writes detected',
      confidence: 95,
      status: 'active',
      details: 'Detected 47 write attempts to critical registers using function code 16',
      canRevert: true,
      color: 'red'
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 5 * 60000),
      type: 'RECOMMENDATION',
      icon: '⚠️',
      device: '192.168.5.226',
      deviceType: 'Workstation',
      reason: 'Port scan detected',
      confidence: 87,
      status: 'pending',
      details: '12 sequential connection attempts to multiple Modbus ports',
      canApprove: true,
      color: 'yellow'
    },
    {
      id: 3,
      timestamp: new Date(Date.now() - 8 * 60000),
      type: 'CLEARED',
      icon: '✅',
      device: '192.168.5.50',
      deviceType: 'PLC',
      reason: 'Normal traffic pattern restored',
      confidence: 92,
      status: 'completed',
      details: 'No anomalies detected in last 30 minutes',
      color: 'green'
    },
    {
      id: 4,
      timestamp: new Date(Date.now() - 15 * 60000),
      type: 'ANALYZING',
      icon: '🟣',
      device: '192.168.5.51',
      deviceType: 'PLC',
      reason: 'Unusual traffic pattern detected',
      confidence: 78,
      status: 'analyzing',
      details: 'AI is analyzing communication patterns for anomalies',
      color: 'purple'
    }
  ]);

  // Format timestamp for display
  const formatTimeAgo = (timestamp) => {
    const minutes = Math.floor((Date.now() - timestamp) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 min ago';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 hour ago';
    return `${hours} hours ago`;
  };

  // Get AI status for a device
  const getDeviceAIStatus = (deviceIp) => {
    const deviceActions = aiActions.filter(a => a.device === deviceIp);
    if (deviceActions.length === 0) return { status: 'protected', icon: '🤖', color: 'green', text: 'PROTECTED' };
    
    const mostRecent = deviceActions.sort((a, b) => b.timestamp - a.timestamp)[0];
    
    if (mostRecent.type === 'ISOLATION') {
      return { status: 'isolated', icon: '🛡️', color: 'red', text: 'ISOLATED' };
    }
    if (mostRecent.type === 'ANALYZING') {
      return { status: 'analyzing', icon: '🟣', color: 'purple', text: 'ANALYZING' };
    }
    if (mostRecent.type === 'RECOMMENDATION') {
      return { status: 'warning', icon: '⚠️', color: 'yellow', text: 'ALERT' };
    }
    return { status: 'protected', icon: '🤖', color: 'green', text: 'PROTECTED' };
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/edge-nodes/`, { headers: apiKeyHeader }),
        axios.get(`${process.env.REACT_APP_API_URL}/events/?limit=500`, { headers: apiKeyHeader })
      ]);
      
      const nodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];
      
      setEdgeNodes(nodes);
      setAllEvents(events);

      // Only set initial node on first load
      if (!selectedNode && nodes.length > 0) {
        const firstNode = nodes[0];
        console.log('🔍 Initial node selection:', firstNode.edge_node_id);
        setSelectedNode(firstNode);
        setViewMode(firstNode.status === 'Online' ? 'live' : 'historical');
      } else {
        console.log('🔍 Keeping selected node:', selectedNode?.edge_node_id);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch topology data:', err);
      setError(err.message || 'Failed to load network topology');
      setLoading(false);
    }
  }, [selectedNode]);

  useEffect(() => {
    fetchData();
    // Refresh every 3 minutes
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Build topology when data changes
  useEffect(() => {
    if (selectedNode && allEvents.length > 0) {
      buildTopology();
    }
  }, [selectedNode, allEvents]);

  // Build topology visualization
  const buildTopology = () => {
    if (!selectedNode) return;

    const nodeEvents = allEvents.filter(e => e.celerway_edge_node_id === selectedNode.edge_node_id);
    
    // Discover devices from events
    const deviceMap = new Map();
    
    nodeEvents.forEach(event => {
      [event.source_ip, event.destination_ip].forEach(ip => {
        if (ip && ip !== '0.0.0.0' && !ip.startsWith('192.168.5.73')) {
          if (!deviceMap.has(ip)) {
            deviceMap.set(ip, {
              ip,
              protocols: new Set(),
              events: [],
              alert: false
            });
          }
          const device = deviceMap.get(ip);
          if (event.protocol) device.protocols.add(event.protocol.toLowerCase());
          device.events.push(event);
          if (event.severity === 'Critical' || event.severity === 'High') {
            device.alert = true;
          }
        }
      });
    });

    const discoveredDevices = Array.from(deviceMap.values()).map((device, idx) => {
      const protocols = Array.from(device.protocols);
      let type = 'unknown';
      let icon = '🖥️';
      let label = 'Device';

      if (protocols.includes('modbus')) {
        type = 'plc';
        icon = '🏭';
        label = 'PLC';
      } else if (protocols.includes('opcua')) {
        type = 'scada';
        icon = '📊';
        label = 'SCADA';
      }

      return {
        id: `device-${idx}`,
        type,
        ip: device.ip,
        icon,
        label,
        protocol: protocols[0],
        eventCount: device.events.length,
        alert: device.alert,
        x: 0,
        y: 0
      };
    });

    // Layout calculations
    const width = svgRef.current?.clientWidth || 1200;
    const height = svgRef.current?.clientHeight || 800;

    const cloudNode = {
      id: 'cloud',
      type: 'cloud',
      label: 'AI Sentry Cloud',
      x: width / 2,
      y: 100
    };

    const agentNode = {
      id: 'agent',
      type: 'agent',
      label: selectedNode.edge_node_id,
      status: selectedNode.status,
      ip: selectedNode.ip_address,
      x: width / 2,
      y: 300
    };

    // Position devices in a grid below agent
    const devicesPerRow = Math.ceil(Math.sqrt(discoveredDevices.length));
    const deviceSpacing = 200;
    const startY = 500;
    const totalWidth = (devicesPerRow - 1) * deviceSpacing;
    const startX = (width - totalWidth) / 2;

    discoveredDevices.forEach((device, idx) => {
      const row = Math.floor(idx / devicesPerRow);
      const col = idx % devicesPerRow;
      device.x = startX + col * deviceSpacing;
      device.y = startY + row * 200;
    });

    // Create edges
    const newEdges = [
      {
        path: `M ${cloudNode.x},${cloudNode.y + 50} L ${agentNode.x},${agentNode.y - 60}`,
        className: 'stroke-2 stroke-blue-400 fill-none'
      },
      ...discoveredDevices.map(device => ({
        path: `M ${agentNode.x},${agentNode.y + 60} L ${device.x},${device.y - 60}`,
        className: 'stroke-2 stroke-gray-500 fill-none stroke-dasharray-4'
      }))
    ];

    setNodes([cloudNode, agentNode, ...discoveredDevices]);
    setEdges(newEdges);
    
    // Update stats
    setStats({
      online: edgeNodes.filter(n => n.status === 'Online').length,
      offline: edgeNodes.filter(n => n.status === 'Offline').length,
      devices: discoveredDevices.length,
      alerts: discoveredDevices.filter(d => d.alert).length
    });
  };

  // Render cloud node
  const renderCloudNode = (node) => (
    <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
      <rect
        x="-100"
        y="-40"
        width="200"
        height="80"
        rx="12"
        className="fill-blue-900 stroke-2 stroke-blue-400"
      />
      <text
        x="0"
        y="-10"
        textAnchor="middle"
        className="text-sm font-semibold fill-white"
      >
        ☁️ {node.label}
      </text>
      <text
        x="0"
        y="10"
        textAnchor="middle"
        className="text-xs fill-gray-300"
      >
        Management Plane
      </text>
    </g>
  );

  // Render agent node
  const renderAgentNode = (node) => (
    <g key={node.id} transform={`translate(${node.x}, ${node.y})`}>
      <rect
        x="-120"
        y="-60"
        width="240"
        height="120"
        rx="12"
        className={`fill-gray-800 stroke-2 ${
          node.status === 'Online' ? 'stroke-green-400' : 'stroke-red-400'
        }`}
      />
      <circle
        cx="90"
        cy="-40"
        r="8"
        className={node.status === 'Online' ? 'fill-green-400 animate-pulse' : 'fill-red-400'}
      />
      <text
        x="0"
        y="-25"
        textAnchor="middle"
        className="text-base font-semibold fill-white"
      >
        🔌 Edge Agent
      </text>
      <text
        x="0"
        y="0"
        textAnchor="middle"
        className="text-sm fill-gray-300"
      >
        {node.label}
      </text>
      <text
        x="0"
        y="20"
        textAnchor="middle"
        className="text-xs fill-gray-400"
      >
        {node.status}
      </text>
      {node.ip && (
        <text
          x="0"
          y="38"
          textAnchor="middle"
          className="text-xs fill-gray-500 font-mono"
        >
          {node.ip}
        </text>
      )}
    </g>
  );

  // Render device node with AI overlays
  const renderDeviceNode = (device) => {
    const aiStatus = getDeviceAIStatus(device.ip);
    
    const getBorderClass = () => {
      if (aiStatus.status === 'isolated') return 'stroke-red-500';
      if (aiStatus.status === 'analyzing') return 'stroke-purple-500';
      if (aiStatus.status === 'warning') return 'stroke-yellow-500';
      if (device.alert) return 'stroke-red-400';
      if (device.type === 'plc' || device.type === 'scada') return 'stroke-blue-400';
      return 'stroke-gray-600';
    };

    return (
      <g key={device.id} transform={`translate(${device.x}, ${device.y})`}>
        {/* Device Card */}
        <rect
          x="-80"
          y="-60"
          width="160"
          height="120"
          rx="8"
          className={`fill-gray-800 stroke-2 ${getBorderClass()} cursor-pointer transition-all ${
            aiStatus.status === 'analyzing' ? 'animate-pulse' : ''
          }`}
        />

        {/* AI Status Badge - Top Right */}
        {aiStatus.text && (
          <>
            <rect
              x="20"
              y="-52"
              width="56"
              height="18"
              rx="9"
              className={`fill-${aiStatus.color}-500`}
            />
            <text
              x="48"
              y="-40"
              textAnchor="middle"
              className="text-[10px] font-bold fill-white"
            >
              {aiStatus.icon} {aiStatus.text}
            </text>
          </>
        )}

        {/* Alert Badge - Top Left */}
        {device.alert && (
          <>
            <circle cx="-60" cy="-50" r="8" className="fill-red-500 animate-pulse" />
            <text
              x="-60"
              y="-47"
              textAnchor="middle"
              className="text-xs font-bold fill-white"
            >
              !
            </text>
          </>
        )}

        {/* Device Icon */}
        <text
          x="0"
          y="-15"
          textAnchor="middle"
          className="text-3xl"
        >
          {device.icon}
        </text>

        {/* Device Label */}
        <text
          x="0"
          y="10"
          textAnchor="middle"
          className="text-sm font-semibold fill-white"
        >
          {device.label}
        </text>

        {/* IP Address */}
        <text
          x="0"
          y="26"
          textAnchor="middle"
          className="text-xs fill-gray-400 font-mono"
        >
          {device.ip}
        </text>

        {/* Protocol Badge */}
        {device.protocol && (
          <>
            <rect
              x="-25"
              y="32"
              width="50"
              height="16"
              rx="8"
              className="fill-gray-700"
            />
            <text
              x="0"
              y="43"
              textAnchor="middle"
              className="text-[10px] fill-blue-400 font-semibold"
            >
              {device.protocol.toUpperCase()}
            </text>
          </>
        )}
      </g>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="text-white text-lg">Loading AI Sentry...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center">
              <span className="mr-3">🤖</span>
              AI Sentry Operations Center
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              Autonomous Security • Real-time AI Analysis • Event-Discovered Devices
            </p>
          </div>

          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse mr-2"></div>
              <span className="text-sm text-gray-400">AI Active</span>
            </div>
            
            {stats.online > 0 && (
              <div className="text-sm">
                <span className="text-gray-400">Agents:</span>
                <span className="text-white font-semibold ml-2">{stats.online} Online</span>
              </div>
            )}
            
            {stats.devices > 0 && (
              <div className="text-sm">
                <span className="text-gray-400">Devices:</span>
                <span className="text-white font-semibold ml-2">{stats.devices}</span>
              </div>
            )}
            
            {stats.alerts > 0 && (
              <div className="flex items-center px-3 py-1 bg-red-900/30 border border-red-500 rounded">
                <span className="text-red-400 font-semibold">{stats.alerts}</span>
                <span className="text-red-300 text-sm ml-2">Alerts</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex h-[calc(100vh-5rem)]">
        {/* Left: Topology */}
        <div className="flex-1 flex flex-col">
          {/* Agent Selector */}
          <div className="bg-gray-800 border-b border-gray-700 px-6 py-3">
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Select Edge Agent
            </label>
            <select
              value={selectedNode?.edge_node_id || ''}
              onChange={(e) => {
                const node = edgeNodes.find(n => n.edge_node_id === e.target.value);
                setSelectedNode(node);
                setViewMode(node?.status === 'Online' ? 'live' : 'historical');
              }}
              className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500"
            >
              {edgeNodes.map(node => (
                <option key={node.edge_node_id} value={node.edge_node_id}>
                  {node.edge_node_id} ({node.status})
                </option>
              ))}
            </select>
          </div>

          {/* Topology Canvas */}
          <div className="flex-1 overflow-auto bg-gradient-to-br from-gray-900 to-gray-800">
            {error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-red-400">{error}</div>
              </div>
            ) : (
              <svg
                ref={svgRef}
                width="100%"
                height="100%"
                className="topology-canvas"
              >
                <defs>
                  <marker
                    id="arrowhead"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3, 0 6" className="fill-gray-500" />
                  </marker>
                </defs>

                {/* Render edges */}
                {edges.map((edge, index) => (
                  <path
                    key={`edge-${index}`}
                    d={edge.path}
                    className={edge.className}
                    markerEnd="url(#arrowhead)"
                  />
                ))}

                {/* Render nodes */}
                {nodes.map((node) => {
                  if (node.type === 'cloud') return renderCloudNode(node);
                  if (node.type === 'agent') return renderAgentNode(node);
                  return renderDeviceNode(node);
                })}
              </svg>
            )}
          </div>
        </div>

        {/* Right: AI Actions Panel */}
        <div className="w-96 bg-gray-800 border-l border-gray-700 overflow-auto">
          <div className="p-6">
            {/* Panel Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center">
                🤖 AI Activity Log
              </h2>
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            </div>

            {/* AI Actions */}
            <div className="space-y-4">
              {aiActions.map(action => (
                <div
                  key={action.id}
                  className={`bg-gray-900 border-2 rounded-lg p-4 transition-all hover:shadow-lg ${
                    action.color === 'red' ? 'border-red-500' :
                    action.color === 'yellow' ? 'border-yellow-500' :
                    action.color === 'purple' ? 'border-purple-500' :
                    'border-green-500'
                  }`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{action.icon}</span>
                      <div>
                        <div className="font-semibold text-white text-sm">
                          {action.type}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatTimeAgo(action.timestamp)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-xs text-gray-400">Confidence</div>
                      <div className="text-lg font-bold text-white">
                        {action.confidence}%
                      </div>
                    </div>
                  </div>

                  {/* Device */}
                  <div className="mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="font-mono text-sm bg-gray-800 px-2 py-1 rounded text-blue-400">
                        {action.device}
                      </span>
                      <span className="text-xs text-gray-500">{action.deviceType}</span>
                    </div>
                  </div>

                  {/* Reason */}
                  <div className="mb-3">
                    <div className="text-sm font-semibold text-white mb-1">
                      {action.reason}
                    </div>
                    <div className="text-xs text-gray-400 leading-relaxed">
                      {action.details}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2 mt-4">
                    {action.canRevert && (
                      <button className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm py-2 rounded font-medium transition-colors">
                        Revert
                      </button>
                    )}
                    {action.canApprove && (
                      <>
                        <button className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm py-2 rounded font-medium transition-colors">
                          Approve
                        </button>
                        <button className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 rounded font-medium transition-colors">
                          Dismiss
                        </button>
                      </>
                    )}
                    {action.status === 'completed' && (
                      <button className="flex-1 bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 rounded font-medium transition-colors">
                        Details
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Status */}
            <div className="mt-6 p-4 bg-blue-900/20 border border-blue-700 rounded-lg">
              <div className="text-blue-300 text-sm">
                <strong>AI Status:</strong> Monitoring {stats.devices} devices across {stats.online} active agents
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AISentryV2;